import { useState } from 'react';
import { BordCheckBox } from 'bord-design-system';
import NudosTextInput from 'components/NudosComponents/NudosTextInput/NudosTextInput';
import NudosMaskedNumberInput from 'components/NudosComponents/NudosMaskedNumberInput/NudosMaskedNumberInput';
import {
  IQuoteEditorFormData,
  IQuotePricingConfig,
  IRequoteParentData,
  TRequotePriceSelection
} from 'types/quoteEditor.types';
import { formatCost } from 'utils/formatNumbers';
import { calculatePrices } from '../../utils/quoteEditorPricing.utils';
import { isDeliveryEstimateRangeValid } from '../../utils/quoteEditorValidation.utils';
import MarkAsCtoModal from '../MarkAsCtoModal/MarkAsCtoModal';
import ProviderDropdown from '../ProviderDropdown/ProviderDropdown';
import RequotePriceSelector from '../RequotePriceSelector/RequotePriceSelector';
import { PRODUCT_CATEGORY } from 'types/category';
import './QuoteEditorForm.scss';

interface IQuoteEditorForm {
  formData: IQuoteEditorFormData;
  requestedUnits: number;
  pricingConfig: IQuotePricingConfig;
  categoryName?: string | null;
  isRequote?: boolean;
  isActiveInStore?: boolean;
  isFormDisabled?: boolean;
  parentData?: IRequoteParentData;
  onChange: (data: IQuoteEditorFormData) => void;
}

/**
 * Formulario de cotización para herramientas no genéricas o sugeridas ya seleccionadas.
 * Los campos de precio (Margen, Platinum, Precio Final) están relacionados entre sí:
 * - Editar Costo o Margen recalcula Platinum y Precio Final.
 * - Platinum es siempre calculado y no editable.
 * - Editar Precio Final activa el override: Platinum y Margen no cambian.
 * - Editar Costo o Margen después de un override lo descarta y recalcula todo.
 * Si `isActiveInStore` es true, los campos de pricing del catálogo (Costo, Margen, Proveedor
 * y Precio Final) quedan deshabilitados y MPN/CTO se ocultan, ya que los datos provienen
 * directamente del producto en tienda.
 */
const QuoteEditorForm = ({
  formData,
  requestedUnits,
  pricingConfig,
  categoryName,
  isRequote,
  isActiveInStore,
  isFormDisabled,
  parentData,
  onChange
}: IQuoteEditorForm) => {
  const disabled = isActiveInStore || isFormDisabled;
  const showCto = !isActiveInStore && !isFormDisabled && (categoryName?.toLowerCase().includes('laptop') ?? false);
  const isCtoCategory = categoryName === PRODUCT_CATEGORY.CTO_LAPTOPS;
  const [isMarkAsCtoOpen, setIsMarkAsCtoOpen] = useState(false);

  const handleChange = (field: keyof IQuoteEditorFormData) => (value: string) => {
    onChange({ ...formData, [field]: value });
  };

  /** Cambia la opción seleccionada en el selector de precio final de recotización. */
  const handlePriceSelectionChange = (selection: TRequotePriceSelection) => {
    onChange({ ...formData, priceSelection: selection });
  };

  /** Cuando cambia el costo, recalcula Platinum y Precio Final descartando cualquier override. */
  const handleCostChange = (value?: string) => {
    const cost = parseFloat(value || '0');
    const margin = parseFloat(formData.margin || '0');
    const { platinum, finalPrice } = calculatePrices(cost, margin, pricingConfig);
    onChange({
      ...formData,
      costWithoutTax: value ?? '',
      platinumPrice: platinum,
      finalPrice,
      finalPriceOverride: false
    });
  };

  /** Cuando cambia el margen, recalcula Platinum y Precio Final descartando cualquier override. */
  const handleMarginChange = (value?: string) => {
    const cost = parseFloat(formData.costWithoutTax || '0');
    const margin = parseFloat(value || '0');
    const { platinum, finalPrice } = calculatePrices(cost, margin, pricingConfig);
    onChange({ ...formData, margin: value ?? '', platinumPrice: platinum, finalPrice, finalPriceOverride: false });
  };

  /** Editar Precio Final activa el override: Platinum y Margen se mantienen. */
  const handleFinalPriceChange = (value?: string) => {
    onChange({ ...formData, finalPrice: value ?? '', finalPriceOverride: true });
  };

  /**
   * Alterna el servicio adicional CTO. En categoría CTO no hace nada (el servicio
   * es obligatorio); al marcarlo manualmente pide confirmación mediante modal.
   */
  const handleCTOToggle = () => {
    if (isCtoCategory) {
      return;
    }
    if (!formData.hasCTO) {
      setIsMarkAsCtoOpen(true);
      return;
    }
    onChange({ ...formData, hasCTO: false });
  };

  const handleConfirmMarkAsCto = () => {
    onChange({ ...formData, hasCTO: true });
    setIsMarkAsCtoOpen(false);
  };

  const taxRateDisplay = `${((pricingConfig.taxRate ?? 0) * 100).toFixed(0)}%`;
  const exchangeRateDisplay = `$${pricingConfig.exchangeRate ?? 0}`;
  const localCurrencySuffix = ` ${pricingConfig.localCurrency}`;
  const saleCurrencySuffix = ` ${pricingConfig.saleCurrency}`;

  const maxDeliveryError = isDeliveryEstimateRangeValid(formData.minDeliveryEstimate, formData.maxDeliveryEstimate)
    ? ''
    : 'El máximo no puede ser menor al mínimo';

  const showPreviousCost = Boolean(isRequote && parentData && formData.costWithoutTax !== parentData.cost);
  const showPreviousMargin = Boolean(isRequote && parentData && formData.margin !== parentData.margin);
  const showPreviousFinalPrice = Boolean(isRequote && parentData && formData.finalPriceOverride);

  const previousCostLabel =
    showPreviousCost && parentData
      ? `Anterior: $${formatCost(parseFloat(parentData.cost) || 0) || 0} ${pricingConfig.localCurrency}`
      : null;
  const previousMarginLabel = showPreviousMargin && parentData ? `Anterior: ${parentData.margin}%` : null;
  const previousFinalPriceLabel =
    showPreviousFinalPrice && parentData
      ? `Anterior: $${formatCost(parseFloat(parentData.finalPrice) || 0) || 0} ${pricingConfig.saleCurrency}`
      : null;

  return (
    <div className={`quoteEditorForm${isActiveInStore ? ' activeInStore' : ''}`}>
      <div className="fieldsWrapper">
        {!isActiveInStore && (
          <div className="formRow">
            <NudosMaskedNumberInput
              label="Estimado de entrega mínimo*"
              placeholder="Semanas"
              maskSufix=" semanas"
              currentValue={formData.minDeliveryEstimate}
              componentSize="w264"
              numberOfDecimals={0}
              integerOnly
              isDeactivated={isFormDisabled}
              handleChange={value => onChange({ ...formData, minDeliveryEstimate: value ?? '' })}
            />
            <NudosMaskedNumberInput
              label="Estimado de entrega máximo*"
              placeholder="Semanas"
              maskSufix=" semanas"
              currentValue={formData.maxDeliveryEstimate}
              componentSize="w264"
              numberOfDecimals={0}
              integerOnly
              errorText={maxDeliveryError}
              isDeactivated={isFormDisabled}
              handleChange={value => onChange({ ...formData, maxDeliveryEstimate: value ?? '' })}
            />
            <NudosMaskedNumberInput
              label="Cantidad"
              currentValue={String(requestedUnits)}
              componentSize="w264"
              numberOfDecimals={0}
              isDeactivated
            />
          </div>
        )}
        <div className="formRow">
          <ProviderDropdown
            value={formData.provider}
            countryId={pricingConfig.countryId}
            countryCode={pricingConfig.countryCode}
            isDeactivated={disabled}
            onChange={(name, id) => onChange({ ...formData, provider: name, providerId: id })}
          />
          <div className="fieldWithHint">
            <NudosMaskedNumberInput
              label="Costo sin IVA*"
              placeholder="0"
              maskPrefix="$"
              maskSufix={localCurrencySuffix}
              numberOfDecimals={2}
              roundToClosest
              currentValue={formData.costWithoutTax}
              componentSize="w264"
              isDeactivated={disabled}
              handleChange={handleCostChange}
            />
            {previousCostLabel && <span className="previousValueHint">{previousCostLabel}</span>}
          </div>
          <div className="compactFieldsGroup">
            <div className="fieldWithHint">
              <NudosMaskedNumberInput
                label="Margen*"
                placeholder="0"
                maskSufix="%"
                currentValue={formData.margin}
                numberOfDecimals={0}
                isDeactivated={disabled}
                handleChange={handleMarginChange}
              />
              {previousMarginLabel && <span className="previousValueHint">{previousMarginLabel}</span>}
            </div>
            <NudosTextInput label="Impuestos*" placeholder="" isFilled defaultValue={taxRateDisplay} isDeactivated />
          </div>
        </div>
        <div className="formRow">
          <NudosTextInput
            label="Tasa de cambio"
            placeholder=""
            isFilled
            defaultValue={exchangeRateDisplay}
            componentSize="w264"
            isDeactivated
          />
          <NudosMaskedNumberInput
            customClassName="calculatedPrice"
            label="Precio con impuestos - calculado"
            maskPrefix="$"
            maskSufix={saleCurrencySuffix}
            numberOfDecimals={2}
            currentValue={formData.platinumPrice}
            componentSize="w264"
            isDeactivated
            placeholder="Se calcula automáticamente"
          />
          <div className="fieldWithHint">
            <NudosMaskedNumberInput
              label="Precio Final (cliente)"
              placeholder="0"
              maskPrefix="$"
              maskSufix={saleCurrencySuffix}
              numberOfDecimals={2}
              currentValue={formData.finalPrice}
              componentSize="w264"
              isDeactivated={disabled}
              handleChange={handleFinalPriceChange}
            />
            {previousFinalPriceLabel && <span className="previousValueHint">{previousFinalPriceLabel}</span>}
          </div>
        </div>
        {!isActiveInStore && (
          <div className="formRow">
            <NudosTextInput
              label="MPN (opcional)"
              placeholder=""
              isFilled={formData.mpn.length > 0}
              defaultValue={formData.mpn}
              componentSize="w264"
              isDeactivated={isFormDisabled}
              handleChange={handleChange('mpn')}
            />
          </div>
        )}
      </div>
      {isRequote && parentData && (
        <RequotePriceSelector
          recotizedPrice={formData.finalPrice}
          previousPrice={parentData.finalPrice}
          saleCurrency={pricingConfig.saleCurrency}
          selected={formData.priceSelection}
          isDeactivated={isFormDisabled}
          onChange={handlePriceSelectionChange}
        />
      )}
      {showCto && (
        <div className="ctoWrapper">
          <div className="ctoLabel">Servicios adicionales</div>
          <div
            className={`ctoCheckBoxButtonWrapper${formData.hasCTO ? ' active' : ''}${isCtoCategory ? ' disabled' : ''}`}
            onClick={handleCTOToggle}
          >
            <BordCheckBox
              className="ctoCheckbox"
              label="CTO"
              isButtonActive={formData.hasCTO}
              isButtonDisabled={isCtoCategory}
              handleClickButton={handleCTOToggle}
            />
          </div>
        </div>
      )}
      {isMarkAsCtoOpen && (
        <MarkAsCtoModal onClose={() => setIsMarkAsCtoOpen(false)} onConfirm={handleConfirmMarkAsCto} />
      )}
    </div>
  );
};

export default QuoteEditorForm;
